import type { ComponentType, SVGProps } from 'react'

/** Joins class names; falsy parts (incl. `node && "cls"` where node is a ReactNode: 0, 0n, "") drop out. */
export function cn(...parts: Array<string | false | null | undefined | 0 | 0n>): string {
  return parts.filter(Boolean).join(' ')
}

/** A lucide-react icon (or any SVG component with the same props). */
export type IconType = ComponentType<SVGProps<SVGSVGElement> & { size?: number | string; strokeWidth?: number | string; absoluteStrokeWidth?: boolean }>

/** Small deterministic hash for avatar gradients and demo data. */
export function hashString(value: string): number {
  let h = 2166136261
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

/** "Ana-Maria Popescu" -> "AP"; one word -> first two letters. */
export function initials(name: string): string {
  const words = name.trim().split(/[\s-]+/).filter(Boolean)
  if (words.length === 0) return '?'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

const roFormatter = new Intl.NumberFormat('ro-RO')
/** 12345.6 -> "12.346" (ro-RO grouping). */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return options ? new Intl.NumberFormat('ro-RO', options).format(value) : roFormatter.format(value)
}

/** 12345 -> "12.345 RON". */
export function formatRon(value: number, fractionDigits = 0): string {
  return `${new Intl.NumberFormat('ro-RO', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }).format(value)} RON`
}

/**
 * Compact form for tight spaces (ro-RO decimal comma): 4812 -> "4,8k", 12840 -> "12,8k",
 * 1250000 -> "1,25M", 950 -> "950". Keep the full value in a title or the accessible table.
 */
export function formatCompact(value: number): string {
  const abs = Math.abs(value)
  const fmt = (v: number, max: number) => new Intl.NumberFormat('ro-RO', { maximumFractionDigits: max }).format(v)
  if (abs >= 1e9) return `${fmt(value / 1e9, abs >= 1e10 ? 0 : 1)}B`
  if (abs >= 1e6) return `${fmt(value / 1e6, abs >= 1e7 ? 1 : 2)}M`
  if (abs >= 1e3) return `${fmt(value / 1e3, abs >= 1e5 ? 0 : 1)}k`
  return fmt(value, 0)
}
